import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useRMList } from 'src/services/api/queries/organizationQueries';
import { cn } from 'src/lib/utils';

const getInitials = (firstName, lastName) =>
  `${(firstName?.[0] || '').toUpperCase()}${(lastName?.[0] || '').toUpperCase()}`;

const formatName = (rm) =>
  `${rm.first_name || ''} ${rm.last_name || ''}`.trim();

const RMListContent = ({
  isLoading,
  error,
  managers,
  selectedRM,
  onSelect,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-[13px]">Loading managers…</span>
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-[13px] text-destructive text-center py-6">{error}</p>
    );
  }
  if (managers.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground text-center py-6">
        No relationship managers available.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto pr-1">
      {managers.map((rm) => {
        const id = rm.id.toString();
        const isSelected = selectedRM === id || selectedRM === rm.id;
        const name = formatName(rm);
        const initials = getInitials(rm.first_name, rm.last_name);
        const meta = rm.role || rm.designation || rm.email || null;

        return (
          <button
            key={rm.id}
            type="button"
            onClick={() => onSelect(id)}
            className={cn(
              'flex items-center gap-3 px-3.5 py-2.5 rounded-lg border text-left transition-all cursor-pointer',
              isSelected
                ? 'border-primary/30 bg-brand-bg-subtle'
                : 'border-border hover:border-primary/20 hover:bg-brand-bg-subtle'
            )}
          >
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-[11.5px] font-semibold flex-shrink-0',
                isSelected
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-[550] text-foreground leading-snug">
                {name}
              </p>
              {meta && (
                <p className="text-[11.5px] text-muted-foreground truncate">
                  {meta}
                </p>
              )}
            </div>
            <div
              className={cn(
                'ml-auto transition-opacity',
                isSelected ? 'opacity-100' : 'opacity-0'
              )}
            >
              <Check className="w-4 h-4 text-primary" strokeWidth={2.5} />
            </div>
          </button>
        );
      })}
    </div>
  );
};

export const RMAssignmentModal = ({
  isOpen,
  onClose,
  organizationId,
  onAssign,
  initialRMId = '',
}) => {
  const { toast } = useToast();
  const [selectedRM, setSelectedRM] = useState(initialRMId);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState(null);

  // Fetched via the shared hook, gated on the modal being open.
  const {
    data: rmResponse,
    isLoading,
    isError,
  } = useRMList({ enabled: isOpen });
  const relationshipManagers = rmResponse?.data?.rm_list || [];
  const displayError =
    error || (isError ? 'Failed to load relationship managers' : null);

  useEffect(() => {
    if (isOpen) {
      setSelectedRM(initialRMId);
    } else {
      setError(null);
    }
  }, [isOpen, initialRMId]);

  const handleAssign = async () => {
    try {
      setIsAssigning(true);
      await onAssign(organizationId, selectedRM);
      onClose();
    } catch (er) {
      toast({
        message: 'Failed to assign relationship manager',
        variant: 'destructive',
      });
      setError('Failed to assign relationship manager');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Relationship Manager</DialogTitle>
          <DialogDescription>
            Select an RM to manage this organisation.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <RMListContent
            isLoading={isLoading}
            error={displayError}
            managers={relationshipManagers}
            selectedRM={selectedRM}
            onSelect={setSelectedRM}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedRM || isAssigning || isLoading}
          >
            {isAssigning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Assigning…
              </>
            ) : (
              'Assign RM'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
